<template>
	<div class="">
		<form @submit.stop.prevent class="w-full">
			<div class="w-full text-sm text-ihh-form-input-entry space-y-2">
				<!-- Name -->
				<div class="w-full md:flex md:justify-between md:space-x-2">
					<div class="w-full mb-2 md:mb-0">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<input
								id="billing-firstName"
								v-model="newAddress.firstName"
								type="text"
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
								placeholder=" "
								autocomplete="given-name"
							/>
							<label
								for="billing-firstName"
								class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								First Name
							</label>
						</div>
					</div>
					<div class="w-full">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<input
								id="billing-lastName"
								v-model="newAddress.lastName"
								type="text"
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
								placeholder=" "
								autocomplete="family-name"
							/>
							<label
								for="billing-lastName"
								class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								Last Name
							</label>
						</div>
					</div>
				</div>

				<!-- Company -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="billing-company"
						v-model="newAddress.company"
						type="text"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
						placeholder=" "
						autocomplete="organization"
					/>
					<label
						for="billing-company"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						<span>Company</span><span class="text-ihh-form-input-optional ml-1">(Optional)</span>
					</label>
				</div>

				<!-- Military Address Toggle -->
				<div class="m-auto my-2 items-center flex w-full">
					<div class="flex items-center m-auto w-full">
						<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
							<input type="checkbox" v-model="isMilitaryAddress" class="absolute opacity-0 w-0 h-0" />
							<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
						</label>
						<div class="text-ihh-slate-gray text-sm items-center text-start">Billing to a US Military Address (APO/FPO/DPO)</div>
					</div>
				</div>

				<!-- Address -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="billing-autocomplete"
						v-model="newAddress.address1"
						type="text"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
						:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('address1') }"
						placeholder=" "
						ref="addressInput"
						autocomplete="shipping street-address"
					/>
					<label
						for="billing-autocomplete"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Address
					</label>
				</div>

				<!-- Apartment/Suite/Building -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="billing-address2"
						v-model="newAddress.address2"
						type="text"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
						placeholder=" "
						autocomplete="address-line2"
					/>
					<label
						for="billing-address2"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						<span>Apartment/Suite/Building</span><span class="text-ihh-form-input-optional ml-1">(Optional)</span>
					</label>
				</div>

				<!-- City & State -->
				<div class="w-full md:flex md:justify-between md:space-x-2">
					<div class="w-full mb-2 md:mb-0">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<!-- Military City Dropdown -->
							<select
								v-if="isMilitaryAddress"
								id="billing-city"
								v-model="newAddress.city"
								class="appearance-none peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
								:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('city') }"
							>
								<option value="" disabled>Select military city</option>
								<option v-for="city in militaryCities" :key="city.code" :value="city.code">{{ city.code }} - {{ city.name }}</option>
							</select>
							<!-- Regular City Input -->
							<input
								v-else
								id="billing-city"
								v-model="newAddress.city"
								type="text"
								class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
								:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('city') }"
								placeholder=" "
								autocomplete="address-level2"
							/>
							<label
								for="billing-city"
								class="pointer-events-none absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								{{ isMilitaryAddress ? 'Military City' : 'City' }}
							</label>
						</div>
					</div>
					<div class="w-full">
						<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
							<select
								id="billing-stateOrProvince"
								v-model="newAddress.stateOrProvince"
								type="text"
								class="appearance-none peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
								:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('stateOrProvince') }"
								placeholder=" "
								autocomplete="address-level1"
							>
								<option value="" disabled>{{ isMilitaryAddress ? 'Select military state' : 'Select a state' }}</option>
								<template v-if="isMilitaryAddress">
									<option v-for="state in militaryStates" :key="state.code" :value="state.code">{{ state.code }} - {{ state.name }}</option>
								</template>
								<template v-else>
									<option v-for="state in states" :key="state.name" :value="state.name">{{ state.name }}</option>
								</template>
							</select>
							<label
								for="billing-stateOrProvince"
								class="pointer-events-none absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
							>
								{{ isMilitaryAddress ? 'Military State' : 'State/Province' }}
							</label>
						</div>
					</div>
				</div>

				<!-- Postal Code -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						id="billing-postalCode"
						v-model="newAddress.postalCode"
						type="string"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
						:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('postalCode') }"
						placeholder=" "
						autocomplete="postal-code"
					/>
					<label
						for="billing-postalCode"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Postal Code
					</label>
				</div>

				<!-- Country -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<select
						@change="fetchStates"
						v-model="newAddress.countryCode"
						id="billing-countryCode"
						type="text"
						class="appearance-none peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none transition-all duration-300"
						:class="{ 'ring-ihh-dark-salmon border-ihh-dark-salmon ring-1 rounded-md': tempFocusFields.includes('countryCode') }"
						placeholder=" "
						autocomplete="country"
					>
						<option value="" disabled>Select Country</option>
						<option v-for="country in countries" :key="country.isoCode" :value="country.isoCode">{{ country.name }}</option>
					</select>
					<label
						for="billing-countryCode"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Country
					</label>
				</div>

				<!-- Phone number -->
				<div class="relative w-full my-1 rounded border ihh-form-border bg-ihh-form-background transition-all focus-within:border-ihh-dark-salmon">
					<input
						@input="formatPhoneNumber($event)"
						@change="formatPhoneNumber($event)"
						id="billing-phoneNumber"
						:value="formattedPhoneNumber"
						type="tel"
						class="peer w-full border-none bg-transparent px-2.5 pt-6 pb-2 text-sm ihh-form-input-entry focus:outline-none"
						placeholder=" "
						autocomplete="tel"
					/>
					<label
						for="billing-phoneNumber"
						class="absolute left-2.5 top-2 text-xs ihh-form-input-label transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs"
					>
						Phone Number
					</label>
				</div>

				<div class="w-full flex justify-center md:justify-start">
					<button
						@click="saveAddress"
						class="flex w-full justify-center md:max-w-40 text-cendter mt-4 uppercase bg-ihh-dark-salmon hover:bg-ihh-salmon ease-in-out duration-100 transition text-ihh-midnight-gray font-semibold tracking-wide py-3 px-4 text-sm rounded items-center"
					>
						{{ continueButtonText }}
					</button>
				</div>
			</div>
		</form>
	</div>
</template>

<script>
import { Country, State } from 'country-state-city'
import { debounce } from 'lodash'
import { isGoogleMapsReady } from '../../helpers/googleMaps.js'
import { parseGooglePlaceComponents } from '../../helpers/parseGooglePlaceComponents.js'
import { useCheckoutStore } from '../../stores/store.js'
import Swal from 'sweetalert2'

export default {
	inject: ['store'],
	emits: ['continue'],
	props: {
		enableAutoComplete: {
			type: Boolean,
			default: false,
		},
		showAddressForm: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			checkout: useCheckoutStore(),
			countries: [],
			states: [],
			creatingAddress: true,
			updatingAddress: false,
			editingAddressId: null,
			tempFocusFields: [],
			newAddress: {
				address1: '',
				address2: '',
				city: '',
				company: '',
				firstName: '',
				lastName: '',
				phone: '',
				postalCode: '',
				stateOrProvince: '',
				countryCode: 'US',
			},
			addresses: [],
			required: ['firstName', 'lastName', 'address1', 'city', 'stateOrProvince', 'postalCode', 'countryCode'],
			formattedPhoneNumber: '',
			missingFields: [],
			addressValidated: false,
			isMilitaryAddress: false,
			autocompleteInstance: null,
			militaryCities: [
				{ code: 'APO', name: 'Army Post Office' },
				{ code: 'FPO', name: 'Fleet Post Office' },
				{ code: 'DPO', name: 'Diplomatic Post Office' },
			],
			militaryStates: [
				{ code: 'AA', name: 'Armed Forces Americas' },
				{ code: 'AE', name: 'Armed Forces Europe' },
				{ code: 'AP', name: 'Armed Forces Pacific' },
			],
		}
	},
	created() {
		this.fetchCountries()
		this.states = State.getStatesOfCountry('US')
	},

	mounted() {
		if (this.checkout.billingAddressValidated.value) {
			this.copyStoreAddressToLocal()
		}

		setTimeout(() => {
			this.syncPhoneFromDOM('billing-phoneNumber')
		}, 300)
	},

	methods: {
		saveAddress() {
			this.syncPhoneFromDOM('billing-phoneNumber')
			this.validateAddress()
			if (!this.addressValidated) {
				Swal.fire({
					icon: 'warning',
					title: 'Missing Information',
					html: this.missingFields.length
						? `<div class="text-center mb-3">Please enter the following fields to continue:</div>
						   <div class="text-center text-sm px-4 py-3 bg-gray-50 rounded-md mx-auto max-w-xs">
						     ${this.missingFields.map((field) => `<div class="py-1 border-b border-gray-200 last:border-0">${field}</div>`).join('')}
						   </div>`
						: 'Please enter all required fields to continue',
					confirmButtonColor: '#e59789',
				})
				return
			}

			this.updateAddress(JSON.parse(JSON.stringify(this.newAddress)))
		},
		copyStoreAddressToLocal() {
			this.newAddress.address1 = this.checkout.billing.billingAddress.address1
			this.newAddress.address2 = this.checkout.billing.billingAddress.address2
			this.newAddress.city = this.checkout.billing.billingAddress.city
			this.newAddress.company = this.checkout.billing.billingAddress.company
			this.newAddress.countryCode = this.checkout.billing.billingAddress.countryCode
			this.newAddress.firstName = this.checkout.billing.billingAddress.firstName
			this.newAddress.lastName = this.checkout.billing.billingAddress.lastName
			this.newAddress.phone = this.checkout.billing.billingAddress.phone
			this.newAddress.postalCode = this.checkout.billing.billingAddress.postalCode
			this.newAddress.stateOrProvince = this.checkout.billing.billingAddress.stateOrProvince
			this.newAddress.hcProvider = this.checkout.billing.billingAddress.hcProvider
			this.newAddress.dueDate = this.checkout.billing.billingAddress.dueDate

			if (this.checkout.billing.billingAddress?.id) {
				this.newAddress.id = this.checkout.billing.billingAddress.id
			}

			const militaryCityCodes = ['APO', 'FPO', 'DPO']
			const militaryStateCodes = ['AA', 'AE', 'AP']
			this.isMilitaryAddress = militaryCityCodes.includes(this.newAddress.city) || militaryStateCodes.includes(this.newAddress.stateOrProvince)

			this.initializeFormattedPhoneNumber()
		},
		applyTempFocusStyling(fieldNames) {
			// Clear any existing temporary focus fields
			this.tempFocusFields = []

			// Add the new fields to focus
			this.tempFocusFields = [...fieldNames]

			// Remove the focus after 1500ms
			setTimeout(() => {
				this.$nextTick(() => {
					this.tempFocusFields = []
				})
			}, 1200)
		},
		fetchCountries() {
			console.log('fetching countries')
			this.countries = Country.getAllCountries()
		},
		fetchStates() {
			if (this.newAddress.countryCode) {
				this.states = State.getStatesOfCountry(this.newAddress.countryCode)
			}
		},
		fetchStateName(stateCode, countryCode) {
			return State.getStateByCode(stateCode, countryCode).name
		},
		validateAddress() {
			console.log('validating new address...')
			const missingFields = []
			const fieldValidationStatus = []

			if (this.newAddress.phone.length) {
				this.required.push('phone')
			} else {
				this.required = this.required.filter((field) => field !== 'phone')
			}

			// Check validation status for each required field
			for (const field of this.required) {
				let isValid = true
				let displayName = this.getFieldDisplay(field)

				if (!this.newAddress[field]) {
					missingFields.push(displayName)
					isValid = false
				} else if (field === 'phone' && this.required.includes('phone')) {
					// Check if phone is required and has 10 digits
					const digitsOnly = this.newAddress.phone.replace(/\D/g, '')
					if (digitsOnly.length !== 10) {
						missingFields.push('Phone Number (must be 10 digits)')
						isValid = false
						displayName = 'Phone Number'
					}
				}

				// Add field validation status to the array
				fieldValidationStatus.push({
					name: displayName,
					field: field,
					validated: isValid,
				})
			}

			// Emit the validation status for all fields
			// this.$emit('field-validation', fieldValidationStatus)
			this.missingFields = missingFields

			if (missingFields.length > 0) {
				this.addressValidated = false
				console.log(`discovered missing fields in address ${missingFields.join(', ')}`)
				return { missingFields: true, message: `Please enter the following fields: ${missingFields.join(', ')}` }
			} else {
				this.addressValidated = true
				return { missingFields: false }
			}
		},
		getFieldDisplay(field) {
			const displayMap = {
				address1: 'Street Address',
				address2: 'Apt/Suite/Room',
				city: 'City',
				company: 'Company Name',
				firstName: 'First Name',
				lastName: 'Last Name',
				phone: 'Phone Number',
				postalCode: 'Postal Code',
				stateOrProvince: 'State/Province',
				countryCode: 'Country',
			}
			return displayMap[field] || field
		},
		updateAddress(address) {
			this.checkout.setBillingAddress(JSON.parse(JSON.stringify(address)))
			this.$emit('continue')
		},

		formatCountry(countryName) {
			const country = Country.getAllCountries().find((c) => c.name === countryName)
			return country ? country.isoCode : null
		},

		normalizePhoneDigits(value) {
			let digits = value.replace(/\D/g, '')
			if (digits.length === 11 && digits.startsWith('1')) {
				digits = digits.slice(1)
			}
			return digits.slice(0, 10)
		},
		syncPhoneFromDOM(inputId) {
			const phoneInput = document.getElementById(inputId)
			if (phoneInput && phoneInput.value && !this.newAddress.phone) {
				const digits = this.normalizePhoneDigits(phoneInput.value)
				this.newAddress.phone = digits
				this.formatDisplayPhone(digits)
			}
		},
		formatDisplayPhone(input) {
			let formatted = ''
			if (input.length <= 3) {
				formatted = input
			} else if (input.length <= 6) {
				formatted = `${input.slice(0, 3)}-${input.slice(3)}`
			} else {
				formatted = `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`
			}
			this.formattedPhoneNumber = formatted
		},
		formatPhoneNumber(event) {
			const input = this.normalizePhoneDigits(event.target.value)

			// Store the raw digits in the newAddress.phone property
			this.newAddress.phone = input

			this.formatDisplayPhone(input)
		},
		initializeFormattedPhoneNumber() {
			if (this.newAddress && this.newAddress.phone) {
				const input = this.normalizePhoneDigits(this.newAddress.phone)
				this.newAddress.phone = input
				this.formatDisplayPhone(input)
			}
		},
		resetDueDate() {
			console.log('date value: ', this.newAddress.dueDate)
			// Use null instead of empty string for Safari compatibility
			this.newAddress.dueDate = null

			// Use $nextTick to ensure DOM updates properly
			this.$nextTick(() => {
				// Force a re-render of the input
				const dateInput = this.$el.querySelector('input[type="date"]')
				if (dateInput) {
					dateInput.value = ''
				}
			})
		},
		resetAddressForm() {
			this.newAddress = {
				address1: '',
				address2: '',
				city: '',
				company: '',
				countryCode: '',
				firstName: '',
				lastName: '',
				phone: '',
				postalCode: '',
				stateOrProvince: '',
				countryCode: 'US',
			}
			this.formattedPhoneNumber = ''
		},
		copyShippingToBilling() {
			if (!this.checkout.shippingAddressValidated.value) return
			const address = JSON.parse(JSON.stringify(this.checkout.shipping.shippingAddress))
			address.hcProvider = false
			address.dueDate = ''
			delete address?.id
			delete address?.bcId
			this.newAddress = address

			const militaryCityCodes = ['APO', 'FPO', 'DPO']
			const militaryStateCodes = ['AA', 'AE', 'AP']
			this.isMilitaryAddress = militaryCityCodes.includes(this.newAddress.city) || militaryStateCodes.includes(this.newAddress.stateOrProvince)
		},

		initAutoComplete() {
			if (this.isMilitaryAddress || !this.enableAutoComplete || !isGoogleMapsReady()) return

			if (this.autocompleteInstance) return

			this.$nextTick(() => {
				const autocompleteId = 'billing-autocomplete'
				const autocompleteElement = document.getElementById(autocompleteId)

				if (!autocompleteElement) {
					console.error(`Could not find element with id ${autocompleteId}`)
					return
				}

				this.autocompleteInstance = new google.maps.places.Autocomplete(autocompleteElement, { types: ['address'] })

				this.autocompleteInstance.addListener('place_changed', () => {
					const place = this.autocompleteInstance.getPlace()
					const parsed = parseGooglePlaceComponents(place?.address_components)
					if (!parsed) return

					this.newAddress.address1 = parsed.address1
					this.newAddress.city = parsed.city
					this.newAddress.stateOrProvince = parsed.stateOrProvince
					this.newAddress.postalCode = parsed.postalCode
					this.newAddress.countryCode = parsed.countryCode

					this.applyTempFocusStyling(['address1', 'city', 'stateOrProvince', 'postalCode', 'countryCode'])

					if (parsed.countryCode) {
						this.fetchStates()
					}
				})
			})
		},
	},

	computed: {
		continueButtonText() {
			return this.checkout.billingAddressValidated.value ? 'Update' : 'Continue'
		},
	},

	watch: {
		'checkout.shipping.billingMatchesShipping': {
			handler(newVal) {
				if (newVal) {
					this.copyShippingToBilling()
				} else if (!newVal) {
					this.resetAddressForm()
				}
			},
			immediate: false,
		},
		enableAutoComplete: {
			handler(newVal) {
				if (newVal && !this.isMilitaryAddress) {
					this.initAutoComplete()
				}
			},
			immediate: false,
		},
		showAddressForm: {
			handler(newVal) {
				if (newVal && this.checkout.billingAddressValidated.value) {
					this.copyStoreAddressToLocal()
				}
			},
		},
		'newAddress.countryCode': {
			handler(newVal) {
				if (newVal !== 'US') {
					this.required = ['firstName', 'lastName', 'address1', 'city', 'countryCode']
				} else {
					this.required = ['firstName', 'lastName', 'address1', 'city', 'stateOrProvince', 'postalCode', 'countryCode']
				}
			},
		},
		isMilitaryAddress: {
			handler(newVal) {
				if (newVal) {
					this.newAddress.countryCode = 'US'
					this.newAddress.city = ''
					this.newAddress.stateOrProvince = ''
					// Disable autocomplete
					if (this.autocompleteInstance) {
						google.maps.event.clearInstanceListeners(this.autocompleteInstance)
						this.autocompleteInstance = null
					}
					// Remove pac-container (autocomplete dropdown)
					const pacContainers = document.querySelectorAll('.pac-container')
					pacContainers.forEach((el) => el.remove())
				} else if (this.enableAutoComplete) {
					this.initAutoComplete()
				}
			},
		},
	},
}
</script>

<style lang="postcss" scoped></style>
